import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GitMerge, Copy, CaretDown, CaretUp, EyeSlash, Phone, At } from "@phosphor-icons/react";

const REASON = {
  phone: { label: "Mismo teléfono", Icon: Phone },
  email: { label: "Mismo email", Icon: At },
};

function MemberRow({ m, primary }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`dup-member-${m.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{m.name || "Sin nombre"}</div>
        <div className="text-muted-foreground truncate font-mono text-[11px]">{m.phone || "—"} · {m.email || "—"}</div>
      </div>
      <div className="text-[10px] text-muted-foreground whitespace-nowrap">{m.conversations} conv · {m.leads} leads</div>
      <Badge className={primary ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" : "bg-rose-500/10 text-rose-600 border-rose-500/30"} variant="outline">
        {primary ? "Se conserva" : "Se fusiona"}
      </Badge>
    </div>
  );
}

function DuplicateGroup({ g, busy, onMerge, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border rounded-md bg-background/60" data-testid={`dup-group-${g.primary_id}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border flex-wrap">
        {g.reasons.map((r) => {
          const { label, Icon } = REASON[r] || { label: r, Icon: Copy };
          return (
            <Badge key={r} variant="outline" className="gap-1 text-[10px] uppercase tracking-wider">
              <Icon size={11} weight="bold" /> {label}
            </Badge>
          );
        })}
        <span className="text-[11px] text-muted-foreground">{g.members.length} contactos</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => onDismiss(g)} data-testid={`dup-dismiss-${g.primary_id}`}>
            <EyeSlash size={12} /> No es duplicado
          </Button>
          <Button size="sm" className="h-7 gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white" disabled={busy} onClick={() => onMerge(g)} data-testid={`dup-merge-${g.primary_id}`}>
            <GitMerge size={12} weight="bold" /> {busy ? "Fusionando…" : "Fusionar"}
          </Button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {(expanded ? g.members : g.members.slice(0, 4)).map((m) => <MemberRow key={m.id} m={m} primary={m.id === g.primary_id} />)}
        {g.members.length > 4 && (
          <button type="button" className="w-full px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setExpanded((v) => !v)} data-testid={`dup-expand-${g.primary_id}`}>
            {expanded ? "Ver menos" : `+${g.members.length - 4} contactos más`}
          </button>
        )}
      </div>
    </div>
  );
}

export const DuplicateDetector = ({ onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(5);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/customers/duplicates");
      setGroups(data.groups || []);
    } catch { setGroups([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async (g) => {
    const primary = g.members.find((m) => m.id === g.primary_id);
    const sources = g.members.filter((m) => m.id !== g.primary_id);
    if (!window.confirm(`Se fusionarán ${sources.length} contacto(s) dentro de "${primary?.name || "—"}" (el de más actividad). Esta acción es irreversible. ¿Continuar?`)) return;
    setBusyKey(g.group_key);
    let ok = 0;
    try {
      for (const s of sources) {
        await api.post("/customers/merge", { primary_id: g.primary_id, source_id: s.id });
        ok += 1;
      }
      toast.success(`Fusión completa · ${ok} contacto(s) unidos a ${primary?.name || "—"}`);
    } catch (err) {
      toast.error(`Error en fusión (${ok}/${sources.length}): ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusyKey(null);
      await load();
      onMerged?.();
    }
  };

  const dismiss = async (g) => {
    setBusyKey(g.group_key);
    try {
      await api.post("/admin/customers/duplicates/dismiss", { customer_ids: g.members.map((m) => m.id) });
      setGroups((prev) => prev.filter((x) => x.group_key !== g.group_key));
      toast.success("Sugerencia ocultada");
    } catch { toast.error("Error"); } finally { setBusyKey(null); }
  };

  if (!groups.length) return null;
  return (
    <div className="border border-amber-500/40 bg-amber-500/5 rounded-md" data-testid="duplicate-detector">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="duplicate-detector-toggle">
        <Copy size={18} weight="bold" className="text-amber-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold" data-testid="duplicate-detector-count">
            {groups.length} posible{groups.length === 1 ? "" : "s"} grupo{groups.length === 1 ? "" : "s"} de contactos duplicados
          </div>
          <div className="text-xs text-muted-foreground">Coinciden por teléfono normalizado o email. Fusiona en 1 clic; se conserva el contacto con más actividad.</div>
        </div>
        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid="duplicate-detector-list">
          {groups.slice(0, limit).map((g) => (
            <DuplicateGroup key={g.group_key} g={g} busy={busyKey === g.group_key} onMerge={merge} onDismiss={dismiss} />
          ))}
          {groups.length > limit && (
            <Button size="sm" variant="outline" onClick={() => setLimit((l) => l + 10)} data-testid="duplicate-detector-more">
              Ver más ({groups.length - limit})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DuplicateDetector;
